import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { mapTree, skipAuthInterceptor, skipTenantInterceptor } from '@asamco/utils';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';

import { AppSettings } from '../configuration/appsettings';
import { MainMenu, MenuItem } from './menu-item.interface';
import { IMenuService } from './menu-service.interface';
import { IMenuProcessor, MENU_PROCESSOR } from './processing/menu-processor.interface';
import { updateMenuItem } from './utils/update-menu-item';

/**
 * Service to maintain the side-menu in an app. Also from within the iframe
 * @export
 */
@Injectable({
	providedIn: 'root',
})
export class MenuService implements IMenuService {
	/** Contains the original menu data (before processing) */
	private originalMenuData: MainMenu = null;

	/** Array of all menu items for the main menu */
	public mainMenu: MenuItem[] = [];

	private updates$: BehaviorSubject<Partial<MenuItem>[]> = new BehaviorSubject<Partial<MenuItem>[]>([]);

	private refreshMenu$ = new Subject<void>();

	_refreshMenu = combineLatest([this.refreshMenu$, this.updates$])
		.pipe(
			map(([_refresh, updates]) => ({ original: this.originalMenuData, updates })),
			map((data: { original: MainMenu; updates: Partial<MenuItem>[] }) => {
				// Apply updates
				const menuData = data.original.map(mi => {
					const update = data.updates.find(miUpdate => mi.id === miUpdate.id);

					if (update != null) {
						return {
							...mi,
							...update,
						};
					} else {
						return mi;
					}
				});

				// Map to a tree structure and make a copy of every item to prevent the original data gets modified
				return mapTree(menuData, 'children', menuItem => ({ ...menuItem }));
			}),
			switchMap(menu => {
				if (Array.isArray(this.menuProcessors)) {
					// Call each processor on the menu and pass the processed menu to the next processor
					return this.menuProcessors.reduce((pv, cv) => pv.pipe(concatMap(processedMenu => cv.process(processedMenu))), of(menu));
				} else {
					return of(menu);
				}
			})
		)
		.subscribe(newMenu => {
			this.mainMenu = newMenu;
		});

	menuVisibility: boolean;

	/**
	 * Constructor
	 */
	constructor(
		private http: HttpClient,
		private app: AppSettings,
		@Optional() @Inject(MENU_PROCESSOR) private menuProcessors: IMenuProcessor[]
	) {
		// Show menu by default
		this.setVisible(true);
	}

	public init() {
		this.http
			.get<MenuItem[]>(`${this.app.deployUrl}assets/menu/menu.json`, {
				headers: skipTenantInterceptor(skipAuthInterceptor()),
			})
			.subscribe(menu => {
				this.setMenu(menu);
			});
	}

	public setMenu(menu: MainMenu) {
		if (!Array.isArray(menu)) {
			console.warn('Menu should be an array, received: ', menu);
			return;
		}

		this.originalMenuData = menu;
		this.updates$.next([]);
		this.refreshMenu$.next();
	}

	public reload(): void {
		this.updates$.next([]);
		this.refreshMenu$.next();
	}

	public setVisible(isVisible: boolean) {
		this.menuVisibility = isVisible;

		if (isVisible) {
			document.body.classList.remove('menu--collapsed');
			document.body.classList.add('menu--visible');
		} else {
			document.body.classList.remove('menu--visible');
			document.body.classList.add('menu--collapsed');
		}
	}

	public toggleVisibility() {
		this.setVisible(!this.menuVisibility);
	}

	/**
	 * Updates the badge of the menu item
	 * @param menuItemId
	 * @param newSettings
	 */
	public updateMenuItem(menuItemId: MenuItem['id'], newSettings: Partial<MenuItem>) {
		const newUpdates = updateMenuItem(this.updates$.getValue() as MainMenu, menuItemId, {
			badge: {
				backgroundColor: newSettings.badge.backgroundColor,
				color: newSettings.badge.color,
				text: newSettings.badge.text,
			},
		});

		this.updates$.next(newUpdates);
	}
}
